/**
 * 送信メールの接続先設定。
 *
 * 招待とログインリンクをここで選んだサービスから送る。
 * 既定は「Supabase 標準」だが、これは 2通/時 に制限されているため、
 * 複数人を同時にログインさせるなら別のサービスを選ぶ必要がある。
 *
 * 【APIキーの扱い】
 * 保存後は画面に返さない（末尾4文字のヒントのみ）。入力欄を空のまま
 * 保存すれば既存の値を保持し、明示的に消したいときだけ削除操作を使う。
 */
import { useEffect, useState } from 'react'
import {
  getEmailSettings,
  updateEmailSettings,
  testEmailSettings,
  ApiError,
  type EmailSettings as Settings,
  type EmailProvider,
} from '../../lib/api.ts'

/** サービスごとの違いを1か所にまとめる。増えてもここだけ触ればよい。 */
const PROVIDERS: {
  id: EmailProvider
  name: string
  summary: string
  needsAccountId?: boolean
  keyLabel?: string
  keyPlaceholder?: string
  setup?: string[]
}[] = [
  {
    id: 'supabase',
    name: 'Supabase 標準',
    summary:
      'アプリからは送らず、Supabase の送信に任せます。設定は不要ですが、' +
      '2通/時 の制限があり、複数人を同時にログインさせられません。',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare Email Service',
    summary:
      'Workers/Pages と同じアカウントで完結します。公開ベータ（Workers Paid プランが必要）。' +
      '1日の送信上限は非公開で、送信実績に応じて自動的に増えます。',
    needsAccountId: true,
    keyLabel: 'API トークン',
    keyPlaceholder: 'Email Sending: Edit 権限のトークン',
    setup: [
      'Cloudflare ダッシュボード → Email Service → Email Sending で送信元ドメインをオンボード（DNS レコードは自動追加）',
      'API トークンを「Email Sending: Edit」権限で作成',
      'アカウントIDとトークンを下に入力。差出人はオンボード済みドメインの任意のアドレスでよい（個別登録は不要）',
    ],
  },
  {
    id: 'resend',
    name: 'Resend',
    summary: '送信ドメインの認証が簡単で、少量なら無料枠で足ります。',
    keyLabel: 'API キー',
    keyPlaceholder: 're_...',
    setup: [
      'resend.com でドメインを追加し、表示された DNS レコードを登録',
      'API Keys から送信権限のキーを作成',
      'キーを下に入力',
    ],
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    summary: '実績のあるサービス。送信者認証（Domain Authentication）が必要です。',
    keyLabel: 'API キー',
    keyPlaceholder: 'SG....',
    setup: [
      'SendGrid で Sender Authentication を済ませる',
      'API Keys から Mail Send 権限のキーを作成',
      'キーを下に入力',
    ],
  },
]

export function EmailSettings() {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [testTo, setTestTo] = useState('')

  useEffect(() => {
    getEmailSettings()
      .then((r) => setSettings(r.settings))
      .catch((e) => setError(e instanceof ApiError ? e.code : String(e)))
  }, [])

  if (!settings) {
    return error ? (
      <p className="error small">読み込めませんでした（{error}）</p>
    ) : (
      <p className="muted small">読み込み中…</p>
    )
  }

  const spec = PROVIDERS.find((p) => p.id === settings.provider) ?? PROVIDERS[0]!
  const patch = (next: Partial<Settings>) =>
    setSettings((prev) => (prev ? { ...prev, ...next } : prev))

  const save = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const r = await updateEmailSettings({
        provider: settings.provider,
        fromAddress: settings.fromAddress ?? '',
        fromName: settings.fromName ?? '',
        cloudflareAccountId: settings.cloudflareAccountId ?? '',
        // 空なら送らない ＝ 既存のキーを保持する
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      })
      setSettings(r.settings)
      setApiKey('')
      setNotice('保存しました')
    } catch (e) {
      setError(`保存できませんでした（${e instanceof ApiError ? e.code : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  const test = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const r = await testEmailSettings(testTo.trim() || undefined)
      setSettings(r.settings)
      setNotice(r.ok ? `${r.to} に送信しました。受信を確認してください` : null)
      if (!r.ok) setError(`送信できませんでした（${r.detail ?? '原因不明'}）`)
    } catch (e) {
      setError(`送信できませんでした（${e instanceof ApiError ? e.code : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  const removeKey = async () => {
    if (!confirm('保存済みの API キーを削除します。よろしいですか？')) return
    setBusy(true)
    try {
      const r = await updateEmailSettings({ apiKey: '' })
      setSettings(r.settings)
      setNotice('API キーを削除しました')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="email-settings">
      <p className="muted small">
        招待とログインリンクのメールをどのサービスから送るかを決めます。
        <strong>Supabase 標準は 2通/時 の制限があります。</strong>
      </p>

      <fieldset className="provider-choice">
        <legend className="small">送信サービス</legend>
        {PROVIDERS.map((p) => (
          <label key={p.id} className={settings.provider === p.id ? 'provider active' : 'provider'}>
            <input
              type="radio"
              name="provider"
              value={p.id}
              checked={settings.provider === p.id}
              onChange={() => patch({ provider: p.id })}
            />
            <span>
              <strong>{p.name}</strong>
              <span className="muted small block">{p.summary}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {spec.setup && (
        <ol className="setup-steps small">
          {spec.setup.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}

      {settings.provider !== 'supabase' && (
        <div className="email-fields">
          <label htmlFor="from-address">差出人アドレス</label>
          <input
            id="from-address"
            type="email"
            value={settings.fromAddress ?? ''}
            onChange={(e) => patch({ fromAddress: e.target.value })}
            placeholder="no-reply@example.com"
          />
          <p className="muted small">
            送信サービス側で認証済みのドメインである必要があります。
          </p>

          <label htmlFor="from-name">差出人名</label>
          <input
            id="from-name"
            value={settings.fromName ?? ''}
            onChange={(e) => patch({ fromName: e.target.value })}
            placeholder="素材ライブラリ"
          />

          {spec.needsAccountId && (
            <>
              <label htmlFor="cf-account">Cloudflare アカウントID</label>
              <input
                id="cf-account"
                value={settings.cloudflareAccountId ?? ''}
                onChange={(e) => patch({ cloudflareAccountId: e.target.value })}
                placeholder="32桁の16進数"
              />
            </>
          )}

          <label htmlFor="api-key">{spec.keyLabel ?? 'API キー'}</label>
          <input
            id="api-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={
              settings.hasApiKey
                ? `保存済み（${settings.apiKeyHint}）— 変更するときだけ入力`
                : (spec.keyPlaceholder ?? '')
            }
          />
          <p className="muted small">
            保存後は画面に表示されません。暗号化して保存され、
            復号鍵はデータベースに置かないため、データベースの複製だけでは復元できません。
            {settings.hasApiKey && (
              <>
                {' '}
                <button className="link" onClick={removeKey} disabled={busy}>
                  保存済みのキーを削除
                </button>
              </>
            )}
          </p>
        </div>
      )}

      <div className="staff-actions">
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? '処理中…' : '保存する'}
        </button>
        {settings.provider !== 'supabase' && settings.hasApiKey && (
          <button disabled={busy} onClick={test}>
            テスト送信
          </button>
        )}
      </div>

      {settings.provider !== 'supabase' && settings.hasApiKey && (
        <div className="test-row">
          <label htmlFor="test-to" className="small">
            テスト送信先（空欄なら自分宛）
          </label>
          <input
            id="test-to"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="自分のメールアドレス"
          />
        </div>
      )}

      {settings.lastTestAt && (
        <p className="small">
          最終テスト: {new Date(settings.lastTestAt).toLocaleString('ja-JP')} —{' '}
          {settings.lastTestOk ? (
            <span className="success">成功</span>
          ) : (
            <span className="error">失敗（{settings.lastTestError}）</span>
          )}
        </p>
      )}

      {notice && <p className="success small">{notice}</p>}
      {error && <p className="error small">{error}</p>}

      <div className="note-block">
        <p className="small">
          <strong>ログイン画面の「メールでログインコードを受け取る」は別経路です。</strong>
          あちらは Supabase が直接送るため、ここの設定では変わりません。
          そちらも同じサービスにするには、Supabase ダッシュボード →
          Project Settings → Authentication → SMTP Settings に同じサービスの
          SMTP 情報を登録してください。
        </p>
        <p className="muted small">
          Cloudflare の場合: ホスト <code>smtp.mx.cloudflare.net</code> / ポート{' '}
          <code>465</code> / ユーザー名 <code>api_token</code> / パスワードは
          「Email Sending: Edit」権限の API トークン（この画面と同じものを使えます）。
          Supabase が 465 の暗黙TLS を話せることは検証済みです。
        </p>
      </div>
    </div>
  )
}
